import { Seo } from "@/components/Seo";
import { ModuleTrialPrompt } from "@/components/ModuleTrialPrompt";
import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Send, Loader2, Crown, Lock, GraduationCap, Flame, Target, BookOpen,
  FileText, Scale, BarChart3, Mic, Trophy, Clock, CheckCircle2, Circle, TrendingUp,
  Paperclip, X,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Disclaimer } from "@/components/Disclaimer";
import { Markdown } from "@/components/Markdown";
import { VoiceButton } from "@/components/VoiceButton";
import { ChatHistory } from "@/components/ChatHistory";
import { PyqPractice } from "@/components/PyqPractice";
import { toast } from "@/components/ui/sonner";

const QUICK_CARDS = [
  { icon: BookOpen, title: "Bare Act Learning", desc: "Learn sections with simple explanations & illustrations", prompt: "Explain Section 3 of the Indian Evidence Act in simple language with an illustration and a memory trick." },
  { icon: FileText, title: "Mock Tests", desc: "Practice timed judiciary MCQs", prompt: "Give me a 10-question timed mock test on the Code of Civil Procedure. Ask one question at a time and score me at the end." },
  { icon: Scale, title: "Landmark Judgments", desc: "Most important SC judgments for exams", prompt: "List the most important Supreme Court judgments for Judiciary 2026 with a one-line ratio for each." },
  { icon: Target, title: "Daily Targets", desc: "Personalized study goals", prompt: "Create today's study target plan for me to clear the Gujarat Judiciary exam, with topics and time slots." },
  { icon: BarChart3, title: "Progress Tracker", desc: "Track subjects & performance", prompt: "Help me plan a weekly revision schedule and tell me how to track my subject-wise progress." },
];

const FEATURE_CHIPS = [
  { label: "Bare Act", prompt: "Simplify an important Bare Act section for judiciary prep with an illustration." },
  { label: "MCQs", prompt: "Generate 20 MCQs on the Indian Contract Act with answers and explanations." },
  { label: "Case Laws", prompt: "Give me the top 15 landmark case laws on Constitutional Law for judiciary exams." },
  { label: "Study Plan", prompt: "Create a 30-day Gujarat Judiciary study plan." },
  { label: "Progress", prompt: "How should I track my preparation progress and revision cycles?" },
  { label: "Viva Practice", prompt: "Take my viva. Ask me interview questions on CrPC one at a time and give feedback." },
  { label: "Judiciary PYQs", prompt: "Give me previous year style questions for the Rajasthan Judiciary exam." },
];

const TRENDING = [
  "New BNSS Important Questions",
  "Top 50 Evidence Act MCQs",
  "Supreme Court Judgments of 2026",
  "Gujarat Judiciary Strategy",
];

const SUGGESTIONS = [
  "Create a 30-day Gujarat Judiciary Study Plan",
  "Quiz me on the Indian Evidence Act",
  "Important Supreme Court Judgments for Judiciary 2026",
  "Explain Res Judicata in simple language",
  "Generate 20 MCQs on the Contract Act",
  "Ask me Viva Questions on CrPC",
];

export default function PrepMate() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [limitModal, setLimitModal] = useState(null);
  const [dash, setDash] = useState(null);
  const [started, setStarted] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [files, setFiles] = useState([]);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const loadDash = useCallback(() => {
    if (!user) return;
    api.get("/prepmate/dashboard").then(({ data }) => setDash(data)).catch(() => {});
  }, [user]);
  useEffect(() => { loadDash(); }, [loadDash]);
  useEffect(() => { if (messages.length === 0) return; endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const loadSession = async (sid) => {
    try {
      const { data } = await api.get(`/prepmate/${sid}/messages`);
      setMessages(data.map((m) => ({ role: m.role, content: m.content })));
      setSessionId(sid);
      setStarted(true);
      setLimitModal(null);
    } catch {
      toast.error("Could not load that conversation.");
    }
  };

  const newChat = () => {
    setMessages([]); setSessionId(null); setInput(""); setStarted(false); setFiles([]);
  };

  const onPickFiles = (list) => {
    const valid = Array.from(list || []).filter((f) => {
      if (f.size > 25 * 1024 * 1024) { toast.error(`"${f.name}" is too large (max 25 MB).`); return false; }
      return true;
    });
    setFiles((prev) => [...prev, ...valid].slice(0, 5));
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    const att = files;
    if ((!content && att.length === 0) || loading) return;
    setStarted(true);
    setInput("");
    const noteSuffix = att.length ? "  " + att.map((f) => `📎 ${f.name}`).join(" ") : "";
    setMessages((m) => [...m, { role: "user", content: (content || "Please analyse the attached material.") + noteSuffix }]);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("message", content);
      if (sessionId) fd.append("session_id", sessionId);
      fd.append("language", "en");
      att.forEach((f) => fd.append("files", f));
      setFiles([]);
      const { data } = await api.post("/prepmate/chat", fd);
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setHistoryKey((k) => k + 1);
      loadDash();
    } catch (e) {
      if (e.response?.status === 402) {
        setLimitModal(e.response.data?.detail?.reason === "free_limit" ? "free" : "guest");
        return;
      }
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", content: msg, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleGoal = async (goalId) => {
    try {
      const { data } = await api.post("/prepmate/goal/toggle", { goal_id: goalId });
      setDash((d) => ({ ...d, goals: data.goals, goals_completed: data.goals_completed }));
    } catch {
      toast.error("Could not update goal.");
    }
  };

  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="prepmate-page">
      <ModuleTrialPrompt />
      <Seo title="PrepMate AI — Judiciary Exam Preparation & PYQ Practice | JusticeVerse" description="Crack the Judiciary, APO and law-officer exams with PrepMate AI — personalised study plans, real PYQ practice, instant-graded mock MCQs, bare-act simplification, daily goals and streaks." />
      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold flex items-center justify-center gap-2">
          <GraduationCap className="w-4 h-4" /> PrepMate AI · Module 05
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-ink mt-4 leading-tight">
          Study Smarter. <span className="gold-text">Clear Judiciary Faster.</span>
        </h1>
        <p className="text-inkmuted mt-5 text-base">
          AI-powered preparation for Judiciary, APO, Assistant Prosecutor &amp; Law Officer exams — your personal mentor for Bare Acts, Mock Tests, PYQs, Judgment Notes &amp; Daily Study Plans.
        </p>
      </div>

      {/* Judiciary Dashboard (signed-in) */}
      {user && dash && (
        <div className="glass-card rounded-3xl p-7 mt-12" data-testid="prep-dashboard">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-serif text-2xl text-ink">{greeting()}, {dash.name} 👋</h2>
            <div className="flex items-center gap-2 glass-card rounded-full px-4 py-2" data-testid="prep-streak">
              <Flame className="w-4 h-4 text-gold" />
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink">
                <span className="text-ink">{dash.streak}</span> Day Streak
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-6">
            {/* Today's goals */}
            <div className="md:col-span-2">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-inkfaint mb-3">Today&apos;s Goals · {dash.goals_completed}/{dash.goals_total}</p>
              <div className="space-y-2">
                {dash.goals.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => toggleGoal(g.id)}
                    data-testid={`goal-${g.id}`}
                    className="w-full flex items-center gap-3 text-left glass-card rounded-xl px-4 py-3 hover:bg-surface transition-all"
                  >
                    {g.done ? <CheckCircle2 className="w-5 h-5 text-gold shrink-0" /> : <Circle className="w-5 h-5 text-inkfaint shrink-0" />}
                    <span className={`text-sm ${g.done ? "line-through text-inkfaint" : "text-ink"}`}>{g.text}</span>
                  </button>
                ))}
              </div>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 content-start">
              <Stat icon={TrendingUp} label="Questions" value={dash.total_questions} />
              <Stat icon={CheckCircle2} label="Topics" value={dash.topics_completed} />
              <Stat icon={Clock} label="Hours" value={dash.study_hours} />
              <Stat icon={Trophy} label="Last Score" value={dash.last_pyq_score != null ? `${dash.last_pyq_score}%` : "—"} />
            </div>
          </div>
        </div>
      )}

      {/* Quick access cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-12">
        {QUICK_CARDS.map((c) => (
          <button
            key={c.title}
            onClick={() => send(c.prompt)}
            data-testid={`quick-card-${c.title.replace(/\s+/g, "-").toLowerCase()}`}
            className="glass-card rounded-2xl p-5 text-left hover:-translate-y-1 transition-all group"
          >
            <c.icon className="w-7 h-7 text-gold" />
            <h3 className="font-serif text-lg text-ink mt-3 group-hover:gold-text">{c.title}</h3>
            <p className="text-xs text-inkfaint mt-1.5 leading-relaxed">{c.desc}</p>
          </button>
        ))}
      </div>

      {/* Chat */}
      <div className="flex items-center justify-between mt-12 mb-3">
        <h2 className="font-serif text-2xl text-ink">Ask PrepMate AI</h2>
        <ChatHistory
          user={user}
          listUrl="/prepmate/sessions"
          normalize={(d) => d.map((s) => ({ id: s.session_id, title: s.title, created_at: s.created_at }))}
          onSelect={loadSession}
          onNew={newChat}
          deleteUrl={(id) => `/prepmate/${id}`}
          deleteAllUrl="/prepmate/sessions"
          activeId={sessionId}
          reloadKey={historyKey}
          label="PrepMate History"
        />
      </div>
      <div className="glass-card rounded-3xl flex flex-col overflow-hidden" data-testid="prep-chat">
        <div className="flex-1 overflow-y-auto p-6 space-y-5 min-h-[340px] max-h-[60vh]">
          {!started && messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <div className="w-14 h-14 rounded-full border border-gold/40 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-2xl text-ink mt-5">👋 Welcome to PrepMate AI</h3>
              <p className="text-sm text-inkmuted mt-2 max-w-md">
                Tell me your target exam and available study hours, and I&apos;ll create a personalized roadmap to clear Judiciary.
              </p>
              <div className="flex flex-wrap gap-2 justify-center mt-6 max-w-2xl">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="prep-suggestion"
                    className="text-xs text-inkmuted border border-hairline rounded-full px-4 py-2 hover:border-gold/40 hover:text-ink transition-all"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-5 py-4 text-sm leading-relaxed ${
                m.role === "assistant" && !m.error ? "" : "whitespace-pre-wrap"
              } ${
                m.role === "user"
                  ? "bg-surface2 border border-hairline text-ink"
                  : m.error
                  ? "bg-red-500/10 border border-red-500/30 text-red-300"
                  : "bg-surface border border-gold/20 text-ink"
              }`}>
                {m.role === "assistant" && !m.error ? <Markdown>{m.content}</Markdown> : m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-surface border border-gold/20 rounded-2xl px-5 py-4 flex items-center gap-2 text-inkmuted text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> PrepMate is thinking…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Feature chips */}
        <div className="px-4 pt-3 flex flex-wrap gap-2 border-t border-hairline">
          {FEATURE_CHIPS.map((c) => (
            <button
              key={c.label}
              onClick={() => send(c.prompt)}
              disabled={loading}
              data-testid={`chip-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="text-[11px] font-mono uppercase tracking-[0.1em] text-inkmuted border border-hairline rounded-full px-3 py-1.5 hover:border-gold/40 hover:text-ink transition-all disabled:opacity-40"
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Input */}
        <div className="p-4">
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid="prep-attachments">
              {files.map((f, i) => (
                <span key={i} className="flex items-center gap-1.5 text-xs bg-surface border border-hairline rounded-full px-3 py-1.5 text-ink">
                  <Paperclip className="w-3 h-3 text-gold" />
                  <span className="max-w-[160px] truncate">{f.name}</span>
                  <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-inkfaint hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2 bg-surface border border-hairline rounded-2xl px-3">
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="prep-upload-btn"
              title="Attach a document or photo"
              className="rounded-full w-11 h-11 flex items-center justify-center text-inkmuted hover:text-gold transition-all shrink-0"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,image/*" className="hidden" data-testid="prep-file-input" onChange={(e) => onPickFiles(e.target.files)} />
            <VoiceButton lang="en" onTranscript={(t) => setInput((p) => (p ? p + " " : "") + t)} className="rounded-full w-11 h-11 flex items-center justify-center hover:bg-surface transition-all shrink-0" />
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              data-testid="prep-input"
              rows={1}
              placeholder="Tell me your target exam & study hours, ask anything, or attach a document / photo…"
              className="flex-1 bg-transparent resize-none outline-none text-ink placeholder:text-inkfaint text-sm py-3 px-2 max-h-32"
            />
            <button
              onClick={() => send()}
              disabled={loading || (!input.trim() && files.length === 0)}
              data-testid="prep-send-btn"
              className="rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-40 w-11 h-11 flex items-center justify-center transition-all shrink-0 mb-2"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Trending */}
      <div className="glass-card rounded-3xl p-7 mt-12" data-testid="prep-trending">
        <h3 className="font-serif text-2xl text-ink flex items-center gap-2"><Flame className="w-5 h-5 text-gold" /> Trending for Judiciary 2026</h3>
        <div className="grid sm:grid-cols-2 gap-2 mt-5">
          {TRENDING.map((t) => (
            <button key={t} onClick={() => send(t)} data-testid="trending-item" className="w-full text-left glass-card rounded-xl px-4 py-3 text-sm text-ink hover:bg-surface transition-all flex items-center gap-2">
              <span className="text-gold">🔥</span> {t}
            </button>
          ))}
        </div>
      </div>

      {/* Real PYQ practice */}
      <div className="mt-8">
        <PyqPractice user={user} onLimit={(reason) => setLimitModal(reason)} onGraded={loadDash} />
      </div>

      <Disclaimer className="mt-8 justify-center" />

      {limitModal && (
        <div className="fixed inset-0 z-[60] bg-canvas/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLimitModal(null)} data-testid="prep-limit-modal">
          <div className="glass-card rounded-3xl max-w-sm w-full p-8 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full border border-gold/40 flex items-center justify-center mx-auto">
              {limitModal === "free" ? <Crown className="w-6 h-6 text-gold" /> : <Lock className="w-6 h-6 text-gold" />}
            </div>
            {limitModal === "guest" ? (
              <>
                <h3 className="font-serif text-2xl text-ink mt-5">You&apos;ve used your free messages</h3>
                <p className="text-sm text-inkmuted mt-2">Guests get 3 free messages. Create a free account to unlock 30 free across all features — or subscribe for unlimited.</p>
                <div className="flex flex-col gap-3 mt-7">
                  <Link to="/register" data-testid="prep-limit-register" className="rounded-full bg-btn text-btnink hover:bg-btnhover px-6 py-3 text-sm font-semibold uppercase tracking-wider">Create Free Account</Link>
                  <Link to="/login" data-testid="prep-limit-login" className="rounded-full bg-surface border border-hairline text-ink hover:bg-surface2 px-6 py-3 text-sm font-semibold uppercase tracking-wider">Sign In</Link>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-serif text-2xl text-ink mt-5">Free limit reached</h3>
                <p className="text-sm text-inkmuted mt-2">You&apos;ve used all 30 free messages. Subscribe for unlimited access to all features.</p>
                <div className="flex flex-col gap-3 mt-7">
                  <Link to="/pricing" data-testid="prep-limit-subscribe" className="rounded-full bg-btn text-btnink hover:bg-btnhover px-6 py-3 text-sm font-semibold uppercase tracking-wider">View Plans</Link>
                  <button onClick={() => setLimitModal(null)} className="text-inkfaint hover:text-ink text-sm">Maybe later</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="glass-card rounded-xl px-4 py-3">
    <Icon className="w-4 h-4 text-gold" />
    <p className="font-serif text-xl text-ink mt-1.5">{value}</p>
    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-inkfaint">{label}</p>
  </div>
);
